import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { Environment } from 'src/environments/environment.model';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly ignoredStatuses = new Set([401, 403]);
  private readonly env = inject<Environment>(EnvironmentInjector);
  private readonly appInsights = inject<AppInsightsService>(AppInsightsServiceInjector);


  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: Error | HttpErrorResponse) => {
        this.handleError(error);
        return throwError(() => error);
      }),
    );
  }

  private handleError(error: Error | HttpErrorResponse): void {
    if (error instanceof HttpErrorResponse && this.ignoredStatuses.has(error.status)) {
      return;
    }

    const { trackedError, properties } = this.buildErrorDetails(error);

    if (!this.env.PRODUCTION) {
      console.error(trackedError, properties);
    }

    this.appInsights.trackException(trackedError, this.constructor.name, properties);
  }

  private buildErrorDetails(error: Error | HttpErrorResponse): {
    trackedError: Error;
    properties: Record<string, string>;
  } {
    if (error instanceof HttpErrorResponse) {
      const normalizedUrl = this.normalizeUrl(error.url ?? '');
      const trackedError = new Error(
        `Http failure response for ${normalizedUrl}: ${error.status} ${error.statusText}`,
      );

      const properties = {
        status: error.status.toString(),
        statusText: error.statusText,
        url: error.url ?? '',
        message: error.message,
      };

      return { trackedError, properties };
    }

    return { trackedError: error, properties: {} };
  }

  private normalizeUrl(url: string): string {
    return url.replace(/\/\d+(?=\/|$)/g, '/:id');
  }
}
